<script lang="ts">
  import * as H from "../settings/history.ts";
  import { app } from "../lib/store.svelte.ts";
  import { shortDate, shortDateTime } from "../lib/format.ts";
  import { groupHistory, type HistoryEntry, type HistoryGroup } from "@split/core";

  let query = $state("");
  const total = $derived(app.events.length);
  const undone = $derived(new Set(app.events.map((e) => e.meta.undoOf).filter((x): x is string => !!x)));

  const matches = (h: HistoryEntry, q: string) =>
    `${h.text} ${h.changes.map((c) => `${c.before} ${c.after}`).join(" ")}`.toLowerCase().includes(q);

  /** Changes grouped the way you'd remember them, newest first; searching opens up every match. */
  const groups = $derived.by(() => {
    const q = query.trim().toLowerCase();
    const all = groupHistory(app.history);
    if (!q) return all;
    const out: HistoryGroup[] = [];
    for (const g of all) {
      const hit = g.entries.filter((h) => matches(h, q));
      if (hit.length) out.push(hit.length === g.entries.length ? g : { ...g, entries: hit, title: `${g.title} (${hit.length} matching)` });
    }
    return out;
  });

  /** Groups by local day, newest first. */
  const days = $derived.by(() => {
    const out: { day: string; items: HistoryGroup[] }[] = [];
    for (const h of groups) {
      const d = new Date(h.to);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const last = out[out.length - 1];
      if (last && last.day === key) last.items.push(h);
      else out.push({ day: key, items: [h] });
    }
    return out;
  });

  const sliderValue = $derived(app.asOfSeq ?? total);
  function slide(v: number) {
    app.asOfSeq = v >= total ? null : v;
  }

  const who = (actor: string) =>
    actor === "import"
      ? H.HISTORY_BY_IMPORT
      : actor.startsWith("token:")
        ? actor.slice(6)
        : actor === "system"
          ? H.HISTORY_BY_SYSTEM
          : actor === "tutoring"
            ? H.HISTORY_BY_TUTORING
            : H.HISTORY_BY_YOU;

  const LABELS: Record<string, string> = {
    "item.add": "Add an item",
    "item.update": "Edit an item",
    "item.remove": "Remove an item",
    "share.set": "Tick or untick someone",
    "payment.add": "Log a payment",
    "payment.update": "Edit a payment",
    "payment.remove": "Remove a payment",
    "person.add": "Add a person",
    "person.update": "Change a person",
    "store.set": "Save a store preset",
    "store.remove": "Remove a store preset",
    "receipt.set": "Set a receipt total",
    "settings.update": "Change settings",
    undo: "Undo a change",
    "import.mark": "Start the Sheet import",
  };

  const canUndo = (h: HistoryEntry) =>
    !app.readOnly && !undone.has(h.event.id) && h.event.type !== "group.created" && h.event.type !== "import.marked";
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>History</h1>
      <p class="lede">Every change is kept forever. Drag back to see the ledger exactly as it was, or undo any single change.</p>
    </div>
  </div>

  <section class="travel">
    <label for="asof" class="tl">
      {#if app.asOfSeq === null}
        Showing now, after {total} changes
      {:else}
        {@const ev = app.events.find((e) => e.seq === app.asOfSeq)}
        Showing the ledger as of {ev ? shortDateTime(ev.at) : "the start"}
      {/if}
    </label>
    <input
      id="asof"
      type="range"
      min="0"
      max={total}
      step="1"
      value={sliderValue}
      oninput={(e) => slide(Number((e.currentTarget as HTMLInputElement).value))}
      aria-valuetext={app.asOfSeq === null ? "Now" : `After change ${app.asOfSeq}`}
    />
    <div class="ends muted"><span>First change</span><span>Now</span></div>
  </section>

  {#if app.outbox.length}
    <section class="waiting">
      <h2>Waiting on this device</h2>
      <ul>
        {#each app.outbox as o (o.cmdId)}
          <li class:failed={o.status === "failed"}>
            <span>
              <strong>{LABELS[o.cmd.type] ?? o.cmd.type}</strong>
              {#if o.status === "failed"}<span class="owes">The server refused this: {o.error}</span>{:else}<span class="muted">will sync automatically</span>{/if}
            </span>
            {#if o.status === "failed"}<button class="btn small" onclick={() => app.discard(o.cmdId)}>Dismiss</button>{/if}
          </li>
        {/each}
      </ul>
    </section>
  {/if}

  <input class="input search" type="search" placeholder="Search history" bind:value={query} aria-label="Search history" />

  {#each days as d (d.day)}
    <h2 class="day">{shortDate(d.day)}</h2>
    <ol class="list">
      {#each d.items as g (g.id)}
        {#if g.entries.length === 1}
          {@render entry(g.entries[0]!)}
        {:else}
          <li class="group">
            <details open={query.trim() !== ""}>
              <summary>
                <span class="seq-time">{new Date(g.to).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}</span>
                <span class="what">
                  <span class="gtitle">{g.title}</span>
                  <span class="by muted">{who(g.actor)} · {g.entries.length} changes</span>
                </span>
              </summary>
              <ol class="list inner">
                {#each g.entries as h (h.event.id)}{@render entry(h)}{/each}
              </ol>
            </details>
          </li>
        {/if}
      {/each}
    </ol>
  {:else}
    <div class="list empty">{query ? "No changes match." : "No changes yet."}</div>
  {/each}
</div>

{#snippet entry(h: HistoryEntry)}
        <li class="entry" class:current={app.asOfSeq === h.event.seq} class:struck={undone.has(h.event.id)}>
          <button class="seq" title="View the ledger right after this change" onclick={() => slide(h.event.seq)}>
            {new Date(h.event.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}
          </button>
          <div class="what">
            <p>{h.text}</p>
            {#if h.changes.length}
              <ul class="changes">
                {#each h.changes as c}<li>{c.field}: <s>{c.before}</s> <span aria-hidden="true">to</span> {c.after}</li>{/each}
              </ul>
            {/if}
            <span class="by muted">{who(h.event.actor)}{undone.has(h.event.id) ? ", undone" : ""}</span>
          </div>
          {#if canUndo(h)}
            <button class="btn small quiet" onclick={() => app.undo(h.event)}>Undo</button>
          {/if}
        </li>
{/snippet}

<style>
  .travel {
    background: var(--surface);
    border: 1px solid var(--rule);
    border-radius: var(--r-lg);
    padding: 16px 18px 12px;
    margin-bottom: 20px;
  }

  .tl {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
  }

  input[type="range"] {
    width: 100%;
    accent-color: var(--ink);
  }

  .ends {
    display: flex;
    justify-content: space-between;
    font-size: var(--t-xs);
  }

  .waiting {
    margin-bottom: 20px;
    padding: 12px 16px;
    border-radius: var(--r-lg);
    background: var(--wash);
  }

  .waiting h2 {
    font-size: var(--t-md);
    margin-bottom: 6px;
  }

  .waiting ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 6px;
    font-size: var(--t-sm);
  }

  .waiting li {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
  }

  .waiting li span span {
    margin-left: 6px;
  }

  .search {
    max-width: 380px;
    margin-bottom: 4px;
  }

  .day {
    font-size: var(--t-sm);
    color: var(--ink-2);
    margin: 18px 4px 8px;
  }

  ol.list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .entry {
    display: grid;
    grid-template-columns: 76px 1fr auto;
    gap: 12px;
    align-items: start;
    padding: 12px 14px;
    background: var(--surface);
  }

  .entry.current {
    background: var(--sel);
  }

  .entry.struck .what > p {
    text-decoration: line-through;
    color: var(--muted);
  }

  .seq {
    border: 0;
    background: none;
    padding: 2px 0;
    text-align: left;
    color: var(--muted);
    font-size: var(--t-sm);
    cursor: pointer;
    text-decoration: underline dotted;
  }

  .what p {
    font-weight: 550;
  }

  .changes {
    margin: 4px 0 0;
    padding: 0;
    list-style: none;
    font-size: var(--t-sm);
    color: var(--ink-2);
  }

  s {
    color: var(--muted);
  }

  .by {
    font-size: var(--t-xs);
  }

  .group details > summary {
    display: flex;
    gap: 12px;
    align-items: baseline;
    padding: 12px 16px;
    background: var(--surface);
    cursor: pointer;
    list-style: none;
  }

  .group details > summary::-webkit-details-marker {
    display: none;
  }

  .group details > summary::after {
    content: "›";
    margin-left: auto;
    color: var(--muted);
    transition: transform 0.15s;
  }

  .group details[open] > summary::after {
    transform: rotate(90deg);
  }

  .seq-time {
    min-width: 64px;
    font-size: var(--t-sm);
    color: var(--muted);
  }

  .gtitle {
    display: block;
    font-weight: 600;
  }

  .inner {
    margin: 0 0 0 24px;
    border-left: 2px solid var(--rule);
  }
</style>
